<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useToast } from '../composables/useToast'
import { isValidThaiPhone, isValidEmail } from '../composables/useValidation'
import { useTrackingStore } from '../store/tracking'

const { showToast } = useToast()
const trackingStore = useTrackingStore()

const form = ref({
  name: '',
  phone: '',
  email: '',
  message: '',
  website: '', // Honeypot field for bots
  captchaAnswer: '', // User's answer to the math question
  _ts: Date.now() // Timestamp to check submit speed
})

const isSubmitting = ref(false)
const submitted = ref(false)
const successContainer = ref(null)

const copySuccess = ref(false)
const copyAddress = () => {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(contact.value.contact_address).then(() => {
      copySuccess.value = true
      showToast('คัดลอกที่อยู่เรียบร้อยแล้ว', 'success')
      setTimeout(() => { copySuccess.value = false }, 2000)
    }).catch(err => {
      console.error('Failed to copy: ', err)
    })
  } else {
    const textArea = document.createElement('textarea')
    textArea.value = contact.value.contact_address
    document.body.appendChild(textArea)
    textArea.select()
    try {
      document.execCommand('copy')
      copySuccess.value = true
      showToast('คัดลอกที่อยู่เรียบร้อยแล้ว', 'success')
      setTimeout(() => { copySuccess.value = false }, 2000)
    } catch (err) {
      console.error('Fallback copy failed', err)
    }
    document.body.removeChild(textArea)
  }
}

// Math CAPTCHA logic
const captchaConfig = ref({ num1: 0, num2: 0, operator: '+', options: [] })

const generateCaptcha = () => {
  const n1 = Math.floor(Math.random() * 10) + 1
  const n2 = Math.floor(Math.random() * 10) + 1
  const correct = n1 + n2
  
  // Generate 2 wrong but plausible answers
  let wrong1, wrong2
  do { wrong1 = correct + Math.floor(Math.random() * 5) - 2 } while (wrong1 === correct || wrong1 < 1)
  do { wrong2 = correct + Math.floor(Math.random() * 8) - 4 } while (wrong2 === correct || wrong2 === wrong1 || wrong2 < 1)
  
  const options = [correct, wrong1, wrong2].sort(() => Math.random() - 0.5)
  
  captchaConfig.value = { num1: n1, num2: n2, operator: '+', options }
  form.value.captchaAnswer = ''
}

// Generate upfront
generateCaptcha()

// Reset form including new timestamp and new CAPTCHA
const resetForm = () => {
  form.value = {
    name: '',
    phone: '',
    email: '',
    message: '',
    website: '',
    captchaAnswer: '',
    _ts: Date.now()
  }
  generateCaptcha()
}

// Dynamic contact info from API
const contact = ref({
  contact_company_name: '',
  contact_address: '',
  contact_map_embed: '',
  contact_working_hours: ''
})

// Multi-entry contact lists
const phones = ref([])
const emails = ref([])
const lines = ref([])
const socialFacebook = ref('')
const socialTiktok = ref('')
const socialYoutube = ref('')

const loadContact = async () => {
  try {
    const res = await fetch('/api/settings/public')
    const data = await res.json()
    if (data.success && data.data) {
      for (const key of Object.keys(contact.value)) {
        if (data.data[key] !== undefined && data.data[key] !== '') {
          contact.value[key] = data.data[key]
        }
      }
      if (data.data.contact_phones) {
        try { const p = typeof data.data.contact_phones === 'string' ? JSON.parse(data.data.contact_phones) : data.data.contact_phones; if (Array.isArray(p)) phones.value = p } catch (e) {}
      }
      if (data.data.contact_emails) {
        try { const e = typeof data.data.contact_emails === 'string' ? JSON.parse(data.data.contact_emails) : data.data.contact_emails; if (Array.isArray(e)) emails.value = e } catch (e) {}
      }
      if (data.data.contact_lines) {
        try { const l = typeof data.data.contact_lines === 'string' ? JSON.parse(data.data.contact_lines) : data.data.contact_lines; if (Array.isArray(l)) lines.value = l } catch (e) {}
      }
      if (data.data.contact_facebook_url) socialFacebook.value = data.data.contact_facebook_url
      if (data.data.contact_tiktok_url) socialTiktok.value = data.data.contact_tiktok_url
      if (data.data.contact_youtube_url) socialYoutube.value = data.data.contact_youtube_url
    }
  } catch (error) {
    console.error('Failed to load contact settings:', error)
  }
}

const submitContact = async () => {
  if (!form.value.name || !form.value.phone || !form.value.email || !form.value.message || !form.value.captchaAnswer) {
    showToast('กรุณากรอกข้อมูลให้ครบถ้วน', 'warning')
    return
  }

  // Validate phone & email format
  if (!isValidThaiPhone(form.value.phone)) {
    showToast('เบอร์โทรศัพท์ไม่ถูกต้อง ต้องขึ้นต้นด้วย 0 และมี 9-10 หลัก', 'error')
    return
  }
  if (!isValidEmail(form.value.email)) {
    showToast('รูปแบบอีเมลไม่ถูกต้อง', 'error')
    return
  }

  // Validate math CAPTCHA on frontend first
  const expectedAnswer = captchaConfig.value.num1 + captchaConfig.value.num2
  if (parseInt(form.value.captchaAnswer) !== expectedAnswer) {
    showToast('คำตอบการยืนยันไม่ถูกต้อง กรุณาลองใหม่', 'error')
    generateCaptcha()
    return
  }

  isSubmitting.value = true
  try {
    const res = await fetch('/api/contact-submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...form.value,
        captchaConfig: captchaConfig.value // send config to backend for validation
      })
    })
    
    if (res.ok) {
      submitted.value = true
      showToast('ส่งข้อความสำเร็จแล้ว เราจะรีบติดต่อกลับโดยเร็วที่สุด', 'success')
      trackingStore.trackEvent({
        type: 'submit_lead',
        leadType: 'contact_form',
        subject: form.value.message.substring(0, 50)
      })
      resetForm()

      await nextTick()
      if (successContainer.value) {
        const y = successContainer.value.getBoundingClientRect().top + window.scrollY - 150
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    } else {
      const errorData = await res.json().catch(() => null)
      showToast(errorData?.error || 'เกิดข้อผิดพลาดในการส่งข้อความ', 'error')
    }
  } catch (error) {
    console.error(error)
    showToast('ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ในขณะนี้', 'error')
  } finally {
    isSubmitting.value = false
  }
}

// Add Structured Data (JSON-LD) for Contact Page
const addStructuredData = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "ติดต่อเรา - Morespace",
    "description": "ติดต่อและสอบถามข้อมูลเพิ่มเติมเกี่ยวกับสินค้าและบริการติดตั้งบ้านเก็บของจาก Morespace",
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": contact.value.contact_company_name,
      "telephone": phones.value.length > 0 ? phones.value[0].value : '',
      "email": emails.value.length > 0 ? emails.value[0].value : '',
      "address": {
        "@type": "PostalAddress",
        "streetAddress": contact.value.contact_address,
        "addressCountry": "TH"
      }
    }
  }

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.text = JSON.stringify(schema)
  script.id = 'json-ld-contact'
  document.head.appendChild(script)
}

onMounted(async () => {
  await loadContact()
  addStructuredData()
})

onUnmounted(() => {
  const script = document.getElementById('json-ld-contact')
  if (script) {
    document.head.removeChild(script)
  }
})
</script>

<template>
  <div class="relative min-h-screen bg-slate-50 dark:bg-[#06090e] text-slate-950 dark:text-slate-50 transition-colors duration-500 overflow-x-hidden selection:bg-emerald-500/30 selection:text-emerald-950 dark:selection:text-emerald-100">
    <!-- Radial Gradients Backdrops -->
    <div class="absolute inset-0 pointer-events-none -z-10 overflow-hidden">
      <div class="absolute -top-[10%] left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-br from-emerald-500/20 via-teal-500/10 to-transparent blur-[120px] rounded-full"></div>
      <div class="absolute top-1/3 right-[-10%] w-[450px] h-[450px] bg-cyan-400/10 dark:bg-cyan-500/5 blur-[90px] rounded-full animate-pulse-slow"></div>
      <div class="absolute bottom-1/4 left-[-10%] w-[500px] h-[500px] bg-emerald-400/10 dark:bg-emerald-600/5 blur-[100px] rounded-full"></div>
      <!-- Fine Grid Pattern -->
      <div class="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
    </div>

    <!-- Hero Header -->
    <section class="relative pt-32 pb-12 lg:pt-40 lg:pb-16 text-center max-w-4xl mx-auto px-4">
      <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50/80 dark:bg-emerald-950/30 backdrop-blur-md border border-emerald-100/50 dark:border-emerald-900/30 shadow-sm mb-6 animate-fade-in-up">
        <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
        <span class="text-xs font-bold tracking-widest uppercase text-emerald-800 dark:text-emerald-300">Contact Us</span>
      </div>
      <h1 class="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-none mb-6 animate-fade-in-up" style="animation-delay: 100ms;">
        <span class="bg-gradient-to-r from-emerald-600 via-teal-500 to-cyan-500 dark:from-emerald-400 dark:via-teal-400 dark:to-cyan-400 bg-clip-text text-transparent">
          ติดต่อสอบถามเรา
        </span>
      </h1>
      <p class="text-base sm:text-lg text-slate-600 dark:text-slate-400 font-light leading-relaxed max-w-2xl mx-auto animate-fade-in-up" style="animation-delay: 200ms;">
        เราพร้อมให้คำปรึกษา แนะนำ และตอบทุกข้อสงสัยเกี่ยวกับการติดตั้งโรงเก็บของสำเร็จรูปพรีเมียม 
        ทีมงานผู้เชี่ยวชาญพร้อมดูแลและบริการวันทำการ {{ contact.contact_working_hours }}
      </p>
    </section>

    <!-- Bento Grid Section -->
    <section class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 relative z-10 animate-fade-in-up" style="animation-delay: 300ms;">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        
        <!-- Left Column: Form Card (7 cols) -->
        <div class="lg:col-span-7 bg-white/70 dark:bg-[#111827]/70 backdrop-blur-xl border border-slate-200/50 dark:border-slate-800/80 rounded-[2.5rem] p-8 md:p-10 shadow-xl shadow-slate-100/50 dark:shadow-none transition-colors duration-500">
          <h2 class="text-2xl font-black text-slate-900 dark:text-white mb-2">ฝากข้อความถึงเรา</h2>
          <p class="text-slate-500 dark:text-slate-400 font-light text-sm mb-8">กรอกข้อมูลให้ครบถ้วน เพื่อให้ทีมผู้บริหารฝ่ายขายติดต่อกลับหาคุณโดยเร็วที่สุด</p>

          <form @submit.prevent="submitContact" v-if="!submitted" class="space-y-6">
            <div class="group relative">
              <label class="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wider">ชื่อ - นามสกุลผู้ติดต่อ <span class="text-red-500">*</span></label>
              <input type="text" v-model="form.name" required placeholder="เช่น นายวริทธิ์ อรุณ" class="w-full border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 rounded-2xl px-5 py-4 focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10 text-slate-900 dark:text-white transition-all font-sans">
            </div>
            
            <div class="group relative">
              <label class="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wider">เบอร์โทรศัพท์ติดต่อ <span class="text-red-500">*</span></label>
              <input type="tel" v-model="form.phone" required placeholder="08x-xxx-xxxx" 
                class="w-full border bg-slate-50/50 dark:bg-slate-900/40 rounded-2xl px-5 py-4 focus:outline-none focus:ring-4 text-slate-900 dark:text-white transition-all font-sans"
                :class="form.phone && !isValidThaiPhone(form.phone) 
                  ? 'border-red-450 dark:border-red-500/80 focus:border-red-500 focus:ring-red-500/10' 
                  : 'border-slate-200 dark:border-slate-800 focus:border-emerald-500 focus:ring-emerald-500/10'"
              >
              <p v-if="form.phone && !isValidThaiPhone(form.phone)" class="text-xs text-red-500 mt-1.5 font-medium">เบอร์โทรต้องขึ้นต้นด้วย 0 และมี 9-10 หลัก</p>
            </div>
            
            <div class="group relative">
              <label class="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wider">อีเมล <span class="text-red-500">*</span></label>
              <input type="email" v-model="form.email" required placeholder="example@email.com" 
                class="w-full border bg-slate-50/50 dark:bg-slate-900/40 rounded-2xl px-5 py-4 focus:outline-none focus:ring-4 text-slate-900 dark:text-white transition-all font-sans"
                :class="form.email && !isValidEmail(form.email) 
                  ? 'border-red-450 dark:border-red-500/80 focus:border-red-500 focus:ring-red-500/10' 
                  : 'border-slate-200 dark:border-slate-800 focus:border-emerald-500 focus:ring-emerald-500/10'"
              >
              <p v-if="form.email && !isValidEmail(form.email)" class="text-xs text-red-500 mt-1.5 font-medium">รูปแบบอีเมลไม่ถูกต้อง</p>
            </div>
            
            <div class="group relative">
              <label class="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wider">เรื่องที่ต้องการติดต่อ / สอบถามข้อสงสัย <span class="text-red-500">*</span></label>
              <textarea v-model="form.message" required rows="4" placeholder="กรุณาระบุข้อความที่ต้องการแจ้งให้เราทราบ" class="w-full border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 rounded-2xl px-5 py-4 focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10 text-slate-900 dark:text-white transition-all resize-none font-sans"></textarea>
            </div>
            
            <!-- AI Human Verification (Modern CAPTCHA) -->
            <div class="bg-gradient-to-br from-slate-50 to-slate-100/50 dark:from-slate-900/50 dark:to-slate-900/10 p-6 rounded-2xl border border-slate-200/60 dark:border-slate-800/60 shadow-inner relative overflow-hidden">
              <div class="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl -mr-10 -mt-10 pointer-events-none"></div>
              
              <div class="flex items-center gap-3 mb-4">
                <div class="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center text-emerald-600 dark:text-emerald-400 border border-emerald-200/20 shrink-0">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                </div>
                <div>
                  <label class="block text-sm font-bold text-slate-800 dark:text-slate-200">ตรวจสอบความเป็นคน <span class="text-red-500">*</span></label>
                  <p class="text-xs text-slate-500 dark:text-slate-400">เลือกผลลัพธ์ที่ถูกต้องเพื่อปลดล็อคการส่งข้อความ</p>
                </div>
              </div>
              
              <div class="flex flex-col sm:flex-row items-center gap-4 relative z-10">
                <div class="bg-white/90 dark:bg-slate-950/80 backdrop-blur-md px-6 py-3.5 rounded-xl border border-slate-200/50 dark:border-slate-800/50 font-mono text-2xl font-black text-slate-800 dark:text-slate-200 tracking-wider shadow-sm flex items-center gap-2">
                  <span class="text-emerald-600 dark:text-emerald-400">{{ captchaConfig.num1 }}</span> 
                  <span class="text-slate-400 font-normal">+</span> 
                  <span class="text-emerald-600 dark:text-emerald-400">{{ captchaConfig.num2 }}</span> 
                  <span class="text-slate-400 font-normal">=</span>
                </div>
                
                <div class="flex gap-3 w-full sm:w-auto mt-2 sm:mt-0">
                  <button 
                    v-for="opt in captchaConfig.options" :key="opt"
                    type="button"
                    @click="form.captchaAnswer = opt"
                    :class="[
                      'flex-1 sm:flex-none w-16 h-14 rounded-xl border text-xl font-bold transition-all duration-300 transform',
                      form.captchaAnswer === opt 
                        ? 'bg-emerald-600 border-emerald-600 text-white shadow-lg shadow-emerald-500/30 scale-105 ring-2 ring-emerald-500/50 ring-offset-2 ring-offset-slate-50 dark:ring-offset-slate-900' 
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-850 text-slate-700 dark:text-slate-350 hover:border-emerald-300 hover:text-emerald-600 dark:hover:border-emerald-700 hover:bg-emerald-50/55 dark:hover:bg-emerald-950/20'
                    ]"
                  >
                    {{ opt }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Honeypot field (hidden from users, visible to bots) -->
            <div class="opacity-0 absolute -z-10 h-0 w-0 overflow-hidden pointer-events-none" aria-hidden="true">
              <label>Website URL</label>
              <input type="text" v-model="form.website" tabindex="-1" autocomplete="nope">
            </div>
            
            <button :disabled="isSubmitting" type="submit" class="w-full relative group overflow-hidden bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-400 text-white font-bold py-4.5 rounded-2xl shadow-lg shadow-emerald-600/25 transition-all duration-300 font-sans tracking-widest text-base">
              <div class="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-shimmer"></div>
              <span class="relative flex items-center justify-center gap-2">
                {{ isSubmitting ? 'กำลังส่งข้อมูล...' : 'ส่งข้อความหาทีมงาน' }}
                <svg v-if="!isSubmitting" class="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
              </span>
            </button>
            <p class="text-xs text-center text-slate-400 font-light mt-4">
              ข้อมูลของคุณจะถูกเก็บรักษาเป็นความลับตามพ.ร.บ. คุ้มครองข้อมูลส่วนบุคคล (PDPA)
            </p>
          </form>

          <!-- Success State -->
          <div ref="successContainer" v-else class="text-center bg-gradient-to-br from-emerald-500/[0.04] to-emerald-500/[0.01] dark:from-emerald-950/15 dark:to-transparent rounded-[2.5rem] p-10 border border-emerald-500/20 shadow-lg shadow-emerald-500/5 relative overflow-hidden">
            <!-- Animated background accent -->
            <div class="absolute -top-10 -left-10 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl"></div>
            
            <div class="relative z-10">
              <div class="w-24 h-24 bg-emerald-50 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-8 border border-emerald-500/20 shadow-md">
                <svg class="w-12 h-12 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"></path></svg>
              </div>
              <h3 class="text-3xl font-black text-slate-900 dark:text-white mb-4">เราได้รับข้อความของคุณแล้ว!</h3>
              <p class="text-slate-500 dark:text-slate-400 mb-10 font-light leading-relaxed max-w-md mx-auto text-base">
                ทีมผู้บริหารฝ่ายขายของเราจะทำการตรวจสอบข้อมูลและรีบติดต่อกลับภายในระยะเวลาทำการ 1-2 ชั่วโมง ขอบคุณที่ให้ความไว้ใจแบรนด์ของเราครับ
              </p>
              <button @click="submitted = false" class="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-100 dark:bg-slate-900 hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-350 font-bold transition-all text-sm shadow-sm border border-slate-200 dark:border-slate-800">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121 9h-4.5"></path></svg>
                ส่งข้อความใหม่อีกครั้ง
              </button>
            </div>
          </div>
        </div>

        <!-- Right Column: Info Cards (5 cols) -->
        <div class="lg:col-span-5 space-y-6">
          
          <!-- Office Address Card -->
          <div class="bg-white/70 dark:bg-[#111827]/70 backdrop-blur-xl border border-slate-200/50 dark:border-slate-800/80 rounded-[2rem] p-8 shadow-xl shadow-slate-100/50 dark:shadow-none hover:shadow-2xl transition-all duration-300 group relative overflow-hidden">
            <div class="absolute -right-8 -bottom-8 w-24 h-24 bg-emerald-500/5 rounded-full blur-xl group-hover:scale-125 transition-transform duration-500"></div>
            
            <div class="flex items-center gap-4 mb-5">
              <div class="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center border border-emerald-200/20 shadow-sm shrink-0">
                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
              </div>
              <div>
                <span class="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Office Address</span>
                <h3 class="font-black text-slate-800 dark:text-slate-200 text-lg leading-tight mt-0.5">ที่ตั้งสำนักงานใหญ่</h3>
              </div>
            </div>
            
            <p class="text-slate-600 dark:text-slate-400 text-sm font-light leading-relaxed mb-6">{{ contact.contact_address }}</p>
            
            <div class="flex items-center gap-3">
              <button @click="copyAddress" class="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-105 hover:bg-slate-200 dark:bg-slate-900 dark:hover:bg-slate-800 text-slate-705 dark:text-slate-300 text-xs font-bold transition-all border border-slate-200 dark:border-slate-850">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path v-if="!copySuccess" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                  <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                {{ copySuccess ? 'คัดลอกสำเร็จ!' : 'คัดลอกที่อยู่' }}
              </button>
              <a v-if="contact.contact_address" :href="'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(contact.contact_address)" target="_blank" class="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition-all shadow-md shadow-emerald-600/10">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"></path></svg>
                เปิดใน Google Maps
              </a>
            </div>
          </div>

          <!-- Phone Bento Card -->
          <div class="bg-white/70 dark:bg-[#111827]/70 backdrop-blur-xl border border-slate-200/50 dark:border-slate-800/80 rounded-[2rem] p-8 shadow-xl shadow-slate-100/50 dark:shadow-none hover:shadow-2xl transition-all duration-300 group relative overflow-hidden">
            <div class="absolute -right-8 -bottom-8 w-24 h-24 bg-teal-500/5 rounded-full blur-xl group-hover:scale-125 transition-transform duration-500"></div>
            
            <div class="flex items-center gap-4 mb-5">
              <div class="w-12 h-12 rounded-xl bg-teal-50 dark:bg-teal-950/40 text-teal-600 dark:text-teal-400 flex items-center justify-center border border-teal-200/20 shadow-sm shrink-0">
                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
              </div>
              <div>
                <span class="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Phone Support</span>
                <h3 class="font-black text-slate-800 dark:text-slate-200 text-lg leading-tight mt-0.5">เบอร์โทรศัพท์ติดต่อ</h3>
              </div>
            </div>
            
            <div class="space-y-3">
              <div v-for="(phone, i) in phones" :key="'phone-'+i" class="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800/60 hover:border-teal-500/30 dark:hover:border-teal-500/20 transition-all group/item">
                <div>
                  <p class="text-[10px] font-bold text-teal-650 dark:text-teal-400 uppercase tracking-wider mb-0.5">{{ phone.name || 'ฝ่ายขาย / สอบถาม' }}</p>
                  <p class="text-sm font-black text-slate-800 dark:text-slate-200">{{ phone.value }}</p>
                </div>
                <a :href="'tel:' + phone.value.replace(/[^0-9+]/g, '')" class="w-9 h-9 rounded-lg bg-teal-55/60 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 flex items-center justify-center group-hover/item:bg-teal-500 group-hover/item:text-white transition-all">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                </a>
              </div>
            </div>
          </div>

          <!-- Digital Channels Card -->
          <div class="bg-white/70 dark:bg-[#111827]/70 backdrop-blur-xl border border-slate-200/50 dark:border-slate-800/80 rounded-[2rem] p-8 shadow-xl shadow-slate-100/50 dark:shadow-none hover:shadow-2xl transition-all duration-300 group relative overflow-hidden">
            <div class="absolute -right-8 -bottom-8 w-24 h-24 bg-cyan-500/5 rounded-full blur-xl group-hover:scale-125 transition-transform duration-500"></div>
            
            <div class="flex items-center gap-4 mb-5">
              <div class="w-12 h-12 rounded-xl bg-cyan-50 dark:bg-cyan-950/40 text-cyan-600 dark:text-cyan-400 flex items-center justify-center border border-cyan-200/20 shadow-sm shrink-0">
                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path></svg>
              </div>
              <div>
                <span class="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Digital Channels</span>
                <h3 class="font-black text-slate-800 dark:text-slate-200 text-lg leading-tight mt-0.5">ช่องทางติดต่อออนไลน์</h3>
              </div>
            </div>
            
            <div class="space-y-4">
              <!-- Emails -->
              <div v-for="(email, i) in emails" :key="'email-'+i" class="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800/60 hover:border-cyan-500/30 dark:hover:border-cyan-500/20 transition-all group/item truncate">
                <div class="truncate mr-2">
                  <p class="text-[10px] font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider mb-0.5">{{ email.name || 'ติดต่อแผนกขาย' }}</p>
                  <p class="text-sm font-black text-slate-850 dark:text-slate-200 truncate">{{ email.value }}</p>
                </div>
                <a :href="'mailto:' + email.value" class="w-9 h-9 rounded-lg bg-cyan-55/65 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 flex items-center justify-center shrink-0 group-hover/item:bg-cyan-550 group-hover/item:bg-cyan-500 group-hover/item:text-white transition-all">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                </a>
              </div>

              <!-- LINE Official -->
              <div v-for="(line, i) in lines" :key="'line-'+i" class="flex items-center justify-between p-3.5 rounded-xl bg-[#06C755]/10 dark:bg-[#06C755]/15 border border-[#06C755]/20 hover:border-[#06C755]/50 transition-all group/item">
                <div>
                  <p class="text-[10px] font-bold text-[#06C755] uppercase tracking-wider mb-0.5">LINE Official ID</p>
                  <p class="text-sm font-black text-slate-800 dark:text-slate-200">{{ line.value }}</p>
                </div>
                <a v-if="line.url" :href="line.url" target="_blank" class="px-4 py-2 rounded-xl bg-[#06C755] hover:bg-[#05b34c] text-white text-xs font-bold transition-all shadow-sm">
                  เพิ่มเพื่อน LINE
                </a>
              </div>
            </div>
          </div>

          <!-- Working Hours & Socials Card -->
          <div class="bg-white/70 dark:bg-[#111827]/70 backdrop-blur-xl border border-slate-200/50 dark:border-slate-800/80 rounded-[2rem] p-8 shadow-xl shadow-slate-100/50 dark:shadow-none hover:shadow-2xl transition-all duration-300 group relative overflow-hidden">
            <div class="absolute -right-8 -bottom-8 w-24 h-24 bg-indigo-500/5 rounded-full blur-xl group-hover:scale-125 transition-transform duration-500"></div>
            
            <div class="flex items-center gap-4 mb-5">
              <div class="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center border border-indigo-250/10 shadow-sm shrink-0">
                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
              </div>
              <div>
                <span class="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Hours & Socials</span>
                <h3 class="font-black text-slate-800 dark:text-slate-200 text-lg leading-tight mt-0.5">เวลาทำการ & โซเชียล</h3>
              </div>
            </div>
            
            <div class="space-y-4">
              <div class="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100/80 dark:border-slate-800/60 flex items-start gap-3">
                <svg class="w-5 h-5 text-indigo-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                <div>
                  <p class="text-xs font-bold text-slate-800 dark:text-slate-200 mb-0.5">เวลาให้บริการติดตั้งและฝ่ายขาย</p>
                  <p class="text-xs text-slate-500 dark:text-slate-400 leading-normal">{{ contact.contact_working_hours }}</p>
                </div>
              </div>

              <!-- Social Links -->
              <div v-if="socialFacebook || socialTiktok || socialYoutube" class="pt-2">
                <p class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-3">โซเชียลมีเดียหลัก</p>
                <div class="flex items-center gap-3">
                  <a v-if="socialFacebook" :href="socialFacebook" target="_blank" class="w-11 h-11 bg-slate-50 dark:bg-slate-900 hover:bg-[#1877F2] border border-slate-150 dark:border-slate-850 text-slate-600 hover:text-white dark:text-slate-300 rounded-xl flex items-center justify-center transition-all duration-300 hover:-translate-y-1 shadow-sm" title="Facebook">
                    <svg class="w-5.5 h-5.5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                  <a v-if="socialTiktok" :href="socialTiktok" target="_blank" class="w-11 h-11 bg-slate-50 dark:bg-slate-900 hover:bg-slate-950 dark:hover:bg-white dark:hover:text-slate-950 border border-slate-150 dark:border-slate-855 text-slate-650 hover:text-white dark:text-slate-350 rounded-xl flex items-center justify-center transition-all duration-300 hover:-translate-y-1 shadow-sm" title="TikTok">
                    <svg class="w-5.5 h-5.5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.73a8.19 8.19 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.16z"/></svg>
                  </a>
                  <a v-if="socialYoutube" :href="socialYoutube" target="_blank" class="w-11 h-11 bg-slate-50 dark:bg-slate-900 hover:bg-[#FF0000] border border-slate-150 dark:border-slate-855 text-slate-650 hover:text-white dark:text-slate-350 rounded-xl flex items-center justify-center transition-all duration-300 hover:-translate-y-1 shadow-sm" title="YouTube">
                    <svg class="w-5.5 h-5.5" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Map Embed (Bottom Container) -->
      <div v-if="contact.contact_map_embed" class="mt-12 bg-white/70 dark:bg-[#111827]/70 backdrop-blur-xl border border-slate-200/50 dark:border-slate-800/80 rounded-[2.5rem] p-4 shadow-xl shadow-slate-100/50 dark:shadow-none" style="animation-delay: 400ms;">
        <div class="rounded-[1.8rem] overflow-hidden border border-slate-200 dark:border-slate-800 shadow-inner relative group h-[400px]">
          <iframe :src="contact.contact_map_embed" width="100%" height="100%" style="border:0;" allowfullscreen referrerpolicy="no-referrer-when-downgrade" class="transition-opacity duration-300 dark:opacity-90 opacity-100"></iframe>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* Scoped Animations & Scrollbar */
.animate-fade-in-up {
  animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.animate-pulse-slow {
  animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-shimmer {
  animation: shimmer 2.5s infinite linear;
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}
</style>
